/*
|--------------------------------------------------------------------------
| Passkey (WebAuthn) registration ceremony - post-login "Create a Passkey" prompt.
|--------------------------------------------------------------------------
|
| Drives the two-step registration ceremony from the `createPasskey` modal
| (resources/views/static/includes/modals/createPasskey.blade.php), shown right after a
| non-passkey login, mirroring how the quickLogin PIN prompt is presented:
|
|   1. POST  {registration options url}                  -> server returns creation options
|   2. navigator.credentials.create(options)              -> browser/authenticator ceremony
|   3. POST  {security url} {action: setAuthProvider,
|             auth_provider: passkey, sub_action: add,
|             passkey_attestation}                         -> server verifies, then stores it
|
| Unlike passkey LOGIN, this runs on an ordinary authenticated page (any page a User can
| land on after logging in), not the dedicated /auth page - so this file loads globally
| via footer-js-scripts.blade.php, exactly like pin.js, and does nothing unless
| #createPasskeyModal is present in the DOM for this request.
|
| WRITTEN IN ES5 STYLE ON PURPOSE, same reasoning as passkey_auth.js: concatenated into a
| combineFiles() bundle, not module-bundled.
|
| EXPOSES EXACTLY ONE GLOBAL, `PasskeyRegister`.
*/
var PasskeyRegister = (function (window, document) {
    'use strict';

    var MODAL_SELECTOR = '#createPasskeyModal';
    var SLIDE_WRAPPER_SELECTOR = '#createPasskeySlideWrapper';
    var busy = false;
    var currentSlide = 0;
    // Milestone 5: true when the ceremony is driven inline from the Profile Security header
    // button (no offer modal); changes how success/errors and the loader are shown.
    var inlineFlow = false;

    /**
     * Base64URL -> ArrayBuffer.
     */
    function toBuffer(base64url) {
        var base64 = String(base64url).replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) {
            base64 += '=';
        }

        var binary = window.atob(base64);
        var bytes = new Uint8Array(binary.length);
        for (var i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return bytes.buffer;
    }

    /**
     * ArrayBuffer -> Base64URL.
     */
    function fromBuffer(buffer) {
        var bytes = new Uint8Array(buffer);
        var binary = '';
        for (var i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

    function modal() {
        return document.querySelector(MODAL_SELECTOR);
    }

    function fieldValue(id) {
        var el = document.getElementById(id);

        return el ? el.value : null;
    }

    function csrfToken() {
        return fieldValue('passkeyRegistrationToken');
    }

    function registrationOptionsUrl() {
        return fieldValue('passkeyRegistrationOptionsUrl');
    }

    function dismissPromptUrl() {
        return fieldValue('passkeyDismissPromptUrl');
    }

    function securityUrl() {
        return fieldValue('passkeySecurityUrl');
    }

    /**
     * Local slide navigation, scoped to this modal only - deliberately NOT the global
     * `slideTo()` from pin.js, which is hardcoded to the PIN modal's own #slideWrapper
     * and slide count.
     */
    function slideTo(index) {
        var wrapper = document.querySelector(SLIDE_WRAPPER_SELECTOR);
        if (!wrapper) {
            return;
        }

        var slides = wrapper.querySelectorAll('.slide');
        if (index < 0 || index >= slides.length) {
            return;
        }

        for (var i = 0; i < slides.length; i++) {
            slides[i].classList.remove('active');
        }
        slides[index].classList.add('active');
        currentSlide = index;
    }

    /**
     * Failure messages render as MARKUP, so a <br> or <b> in our lang strings shows
     * as formatting, never as literal text - the same posture as the app-wide
     * $.alertHandler (displayAlertMessages renders raw HTML), which the inline flow
     * already reports through. That is safe ONLY because everything routed here is
     * our own: the page-rendered PasskeyRegisterLang constants, a server
     * responseJSON.msg resolved from our lang files, or a browser/Error text - and
     * it must stay that way: never route user-echoed input into showError.
     */
    function showError(message) {
        var el = document.getElementById('createPasskeyErrorMessage');
        if (!el) {
            return;
        }

        if (message) {
            el.innerHTML = message;
            el.classList.remove('hide');
        } else {
            el.classList.add('hide');
        }
    }

    function post(url, payload) {
        var body = { _token: csrfToken() };
        for (var key in payload) {
            if (Object.prototype.hasOwnProperty.call(payload, key)) {
                body[key] = payload[key];
            }
        }

        // Wrapped in a native Promise: jQuery 2.x's Deferred.then() only chains
        // something it recognizes via its own .promise() duck-type, not a native
        // Promise (e.g. the one navigator.credentials.create() returns further down
        // this chain) - it would otherwise resolve immediately with the pending
        // Promise object itself instead of waiting for the WebAuthn ceremony.
        return Promise.resolve(window.jQuery.ajax({
            type: 'POST',
            url: url,
            data: body,
            dataType: 'json'
        }));
    }

    /**
     * Ask the server for registration options.
     */
    function requestOptions() {
        return post(registrationOptionsUrl(), {}).then(function (data) {
            // Milestone 5 (proactive Identity Vetting): the server gates this step behind
            // vetting, so an unvetted User gets the standard IV payload instead of options.
            // Hand it to the shared IV modal/resume flow (id-vetting.js) and stop the
            // ceremony here - the biometric never runs for an unvetted User. After the User
            // verifies, the resume re-runs THIS endpoint (now vetted); create() retries the
            // ceremony from there.
            if (data && data.data && data.data.identity_vetting_required) {
                if (typeof window.checkForIdentityVetting === 'function') {
                    window.checkForIdentityVetting(data);
                }

                var ivError = new Error('identity_vetting_required');
                ivError.identityVetting = true;
                throw ivError;
            }

            var options = data && data.data && data.data.options;

            if (!options || !options.challenge) {
                return window.jQuery.Deferred().reject('no_options').promise();
            }

            return options;
        });
    }

    // When the current WebAuthn prompt went up, and the timeout the server issued with it.
    // Recorded here (rather than at each call site) so all three entry points - the offer
    // modal, the Profile inline button and the IV resume - are covered by one place.
    var ceremonyStartedAt = null;
    var ceremonyTimeoutMs = null;

    /**
     * Pick the message for a ceremony that ended in NotAllowedError / AbortError.
     *
     * WebAuthn deliberately reports a deliberate dismissal and an expired ceremony with the
     * SAME NotAllowedError - the spec hides the difference so a site cannot learn whether the
     * User has a credential. The only signal left is how long the prompt was on screen, so a
     * ceremony that survived ~90% of its own timeout is treated as having timed out, and
     * anything quicker as the User dismissing it.
     *
     * Returns undefined when the strings are absent, and showError()/inlineError() then stay
     * silent - the previous behaviour.
     */
    function cancellationMessage() {
        var lang = window.PasskeyRegisterLang || {};
        var limit = (typeof ceremonyTimeoutMs === 'number' && ceremonyTimeoutMs > 0) ? ceremonyTimeoutMs : 60000;
        var elapsed = ceremonyStartedAt ? (Date.now() - ceremonyStartedAt) : 0;

        return (elapsed >= limit * 0.9) ? lang.timedOut : lang.cancelled;
    }

    /**
     * Run the browser ceremony.
     */
    function runCeremony(options) {
        var excludeCredentials = [];
        var i;

        if (options.excludeCredentials) {
            for (i = 0; i < options.excludeCredentials.length; i++) {
                excludeCredentials.push({
                    id: toBuffer(options.excludeCredentials[i].id),
                    type: options.excludeCredentials[i].type
                });
            }
        }

        var publicKey = {
            challenge: toBuffer(options.challenge),
            rp: options.rp,
            user: {
                id: toBuffer(options.user.id),
                name: options.user.name,
                displayName: options.user.displayName
            },
            pubKeyCredParams: options.pubKeyCredParams,
            authenticatorSelection: options.authenticatorSelection,
            attestation: options.attestation,
            timeout: options.timeout,
            excludeCredentials: excludeCredentials
        };

        ceremonyStartedAt = Date.now();
        ceremonyTimeoutMs = (typeof options.timeout === 'number') ? options.timeout : null;

        return window.navigator.credentials.create({ publicKey: publicKey });
    }

    /**
     * Shape the browser's PublicKeyCredential into the JSON the server verifies.
     */
    function serializeAttestation(credential) {
        var response = credential.response;
        var transports = (typeof response.getTransports === 'function') ? response.getTransports() : [];

        return {
            id: credential.id,
            rawId: fromBuffer(credential.rawId),
            type: credential.type,
            response: {
                clientDataJSON: fromBuffer(response.clientDataJSON),
                attestationObject: fromBuffer(response.attestationObject)
            },
            transports: transports
        };
    }

    /**
     * Submit the attestation through the EXISTING setAuthProvider action - not a
     * dedicated endpoint - so it gets Identity Vetting, audit logging and the
     * "was added" notification email for free, exactly like every other provider.
     */
    function submitAttestation(credential) {
        return post(securityUrl(), {
            action: 'setAuthProvider',
            auth_provider: 'passkey',
            sub_action: 'add',
            passkey_attestation: JSON.stringify(serializeAttestation(credential))
        });
    }

    function isCancellation(error) {
        return !!error && (error.name === 'NotAllowedError' || error.name === 'AbortError');
    }

    function setBusy(state) {
        busy = state;

        var button = document.getElementById('createPasskeyBtn');
        if (button) {
            button.classList.toggle('isBusy', !!state);
            var loading = button.querySelector('.loading');
            if (loading) {
                // The "hide" utility class (display:none) is separate from the inline
                // style toggled below - leaving it in place while busy meant the
                // spinner never actually appeared, so the button just looked empty.
                loading.classList.toggle('hide', !state);
                loading.style.display = state ? '' : 'none';
            }

            // The LABEL is deliberately NOT hidden with an inline display here. Two reasons:
            //
            //   1. `$.ajax_prototype`'s complete handler (shared/plugin.js) runs
            //      `$(".submitText").show()` on EVERY request, with no opt-out - it would
            //      simply undo an inline `display:none` a few ms later. (The spinner escapes
            //      that same handler via the `always_visible` class on `.loading`.)
            //   2. `display:none` removes the label from the layout, so the button collapses
            //      to the spinner's size mid-ceremony.
            //
            // Instead the `isBusy` class toggled above drives it from CSS: the label is
            // hidden with `visibility` (so it still occupies its space and the button keeps
            // its exact width/height) and the spinner is absolutely centred over it.
            // See `#createPasskeyBtn` in scss/includes/_modal-content.scss.
        }
    }

    /**
     * Full registration ceremony, triggered by "Create Passkey".
     */
    function create() {
        if (busy) {
            return;
        }

        setBusy(true);
        showError(null);

        requestOptions()
            .then(function (options) {
                return runCeremony(options);
            })
            .then(function (credential) {
                return submitAttestation(credential);
            })
            .then(function (data) {
                // The server always answers with HTTP 200, success or failure alike
                // (see JsonHelper::jsonResponse) - failure is only signalled by
                // data.success === false, exactly like every other setAuthProvider
                // caller (public/resources/js/app/account/profile.js) already checks.
                // Without this, a friendly duplicate/verification-failure response
                // still resolves the promise, so the success slide would show even
                // though nothing was actually stored.
                if (!data || !data.success) {
                    var err = new Error((data && data.msg) || 'passkey registration failed');
                    err.responseJSON = data;
                    throw err;
                }

                setBusy(false);
                slideTo(2);
            })
            .catch(function (error) {
                setBusy(false);

                if (isCancellation(error)) {
                    // Dismissed, or the ceremony expired. Stay on the offer slide and say
                    // which it was - a silent revert is indistinguishable from a dead button.
                    showError(cancellationMessage());

                    return;
                }

                if (error && error.identityVetting) {
                    // Not vetted: the shared IV modal is now showing (requestOptions handed
                    // off to it). Close this passkey modal so only the IV modal is visible;
                    // the ceremony is retried after the User verifies. No error message.
                    var modalEl = modal();
                    if (modalEl && window.jQuery) {
                        window.jQuery(modalEl).modal_close();
                    }

                    return;
                }

                var message = (error && error.responseJSON && error.responseJSON.msg)
                    ? error.responseJSON.msg
                    : null;

                showError(message || (window.PasskeyRegisterLang && window.PasskeyRegisterLang.genericError));
            });
    }

    /**
     * Continue the ceremony with options obtained AFTER Identity Vetting. The proactive IV
     * gate on getRegistrationOptions diverts an unvetted User to the IV modal before the
     * biometric; once verified, the resumed getRegistrationOptions delivers the options
     * (id-vetting.js -> applyActionsinProfiles), which are handed here to finish the
     * registration. Same tail as create(), minus the options request.
     */
    function continueWithOptions(options) {
        if (busy || !options || !options.challenge) {
            return;
        }

        // Inline (header-button) flow: no modal - keep the button loader, finish the
        // ceremony, then reload on success.
        if (inlineFlow) {
            setInlineBusy(true);
            Promise.resolve(runCeremony(options))
                .then(function (credential) {
                    showCeremonyOverlay();
                    return submitAttestation(credential);
                })
                .then(inlineFinish)
                .catch(function (error) {
                    setInlineBusy(false);
                    hideCeremonyOverlay();
                    inlineError(error);
                });

            return;
        }

        var modalEl = modal();
        if (modalEl && window.jQuery) {
            window.jQuery(modalEl).modal_open();
        }

        setBusy(true);
        showError(null);

        Promise.resolve(runCeremony(options))
            .then(function (credential) {
                return submitAttestation(credential);
            })
            .then(function (data) {
                if (!data || !data.success) {
                    var err = new Error((data && data.msg) || 'passkey registration failed');
                    err.responseJSON = data;
                    throw err;
                }

                setBusy(false);
                slideTo(2);
            })
            .catch(function (error) {
                setBusy(false);

                if (isCancellation(error)) {
                    // Same as create(): report dismissal vs timeout rather than reverting
                    // silently, which reads as "the prompt just came back on its own".
                    showError(cancellationMessage());

                    return;
                }

                var message = (error && error.responseJSON && error.responseJSON.msg)
                    ? error.responseJSON.msg
                    : null;

                showError(message || (window.PasskeyRegisterLang && window.PasskeyRegisterLang.genericError));
            });
    }

    /**
     * The Profile Security header "Create a passkey" button (id createPasskeyInlineBtn) and
     * its loader/text spans.
     */
    function inlineButton() {
        return window.jQuery ? window.jQuery('#createPasskeyInlineBtn') : null;
    }

    function setInlineBusy(state) {
        busy = state;

        var $b = inlineButton();
        if (!$b || !$b.length) {
            return;
        }

        if (state) {
            // Lock BOTH dimensions BEFORE hiding the label, so the button keeps its exact
            // size with the spinner centered - otherwise it collapses to the spinner.
            $b.css({
                'width': $b.outerWidth() + 'px',
                'height': $b.outerHeight() + 'px',
                'display': 'inline-flex',
                'align-items': 'center',
                'justify-content': 'center'
            });
            $b.addClass('disabled');
            $b.find('.submitText').addClass('hide');
            $b.find('.loading').removeClass('hide').css('display', '');
        } else {
            $b.css({ 'width': '', 'height': '', 'display': '', 'align-items': '', 'justify-content': '' });
            $b.removeClass('disabled');
            $b.find('.submitText').removeClass('hide');
            $b.find('.loading').addClass('hide').css('display', 'none');
        }
    }

    function inlineError(error) {
        var message;

        if (isCancellation(error)) {
            // Dismissal vs timeout, same as the modal flow. Nothing to say if the strings
            // are missing - fall back to the previous silence rather than an empty alert.
            message = cancellationMessage();
            if (!message) {
                return;
            }

            if (window.jQuery && typeof window.jQuery.alertHandler === 'function') {
                window.jQuery.alertHandler('', message, (typeof alert_box_failure !== 'undefined') ? alert_box_failure : undefined);
            }

            return;
        }

        message = (error && error.responseJSON && error.responseJSON.msg)
            ? error.responseJSON.msg
            : null;
        message = message || (window.PasskeyRegisterLang && window.PasskeyRegisterLang.genericError) || 'Passkey creation failed';

        if (window.jQuery && typeof window.jQuery.alertHandler === 'function') {
            window.jQuery.alertHandler('', message, (typeof alert_box_failure !== 'undefined') ? alert_box_failure : undefined);
        } else if (window.alert) {
            window.alert(message);
        }
    }

    function inlineFinish(data) {
        if (!data || !data.success) {
            var err = new Error((data && data.msg) || 'passkey registration failed');
            err.responseJSON = data;
            throw err;
        }

        // Success: reload so the passkey list updates and the "Create a passkey" button hides
        // (this device now has a passkey). Reload TO the #securityContent hash so Foundation's
        // tab deep-linking re-activates the Security tab the User was on - a plain reload lands
        // on the default (Details) tab.
        try { window.location.hash = 'securityContent'; } catch (e) {}
        window.location.reload();
    }

    /**
     * Full-screen black overlay + spinner shown during the "dead" window AFTER the WebAuthn
     * ceremony (once the User has entered their Windows Hello PIN / biometric) while the
     * attestation is posted and the page reloads - otherwise the page just sits static for a
     * few seconds. Reuses the app-wide `activateOverlayLoader()` (plugin.js, `.global-overlay-loader`),
     * with a self-contained fallback that renders the exact same markup if that helper is not
     * present on the page.
     */
    function showCeremonyOverlay() {
        if (typeof window.activateOverlayLoader === 'function') {
            window.activateOverlayLoader();
            return;
        }
        if (window.jQuery) {
            var $o = window.jQuery('.global-overlay-loader');
            if (!$o.length) {
                window.jQuery('body').append('<div class="global-overlay-loader"><div class="loading"><span class="spinner"></span></div></div>');
                $o = window.jQuery('.global-overlay-loader');
            }
            $o.show().find('.loading').show();
        }
    }

    function hideCeremonyOverlay() {
        if (typeof window.deactivateOverlayLoader === 'function') {
            window.deactivateOverlayLoader();
            return;
        }
        if (window.jQuery) {
            window.jQuery('.global-overlay-loader').hide();
        }
    }

    /**
     * Milestone 5: run the registration ceremony straight from the Profile Security header
     * button - no offer modal. The button shows a loader; an unvetted User is still diverted
     * to the IV modal (requestOptions), and the ceremony resumes after verification via
     * continueWithOptions. On success the page reloads.
     */
    function createInline() {
        if (busy) {
            return;
        }

        inlineFlow = true;
        setInlineBusy(true);

        requestOptions()
            .then(function (options) {
                return runCeremony(options);
            })
            .then(function (credential) {
                // PIN/biometric done - cover the post-ceremony post+reload gap with the overlay.
                showCeremonyOverlay();
                return submitAttestation(credential);
            })
            .then(inlineFinish)
            .catch(function (error) {
                setInlineBusy(false);
                hideCeremonyOverlay();

                if (error && error.identityVetting) {
                    // The IV modal is now showing; the ceremony continues after the User
                    // verifies (continueWithOptions, inline branch). No error, no biometric yet.
                    return;
                }

                inlineError(error);
            });
    }

    /**
     * "Never ask again?" answer.
     */
    function dismiss(neverAskAgain) {
        return post(dismissPromptUrl(), { never_ask_again: neverAskAgain ? '1' : '0' }).then(function () {
            var modalEl = modal();
            if (modalEl && window.jQuery) {
                window.jQuery(modalEl).modal_close();
            }
        });
    }

    // ------------------------------------------------------------------
    // Milestone 6: remove a single passkey from Profile Security.
    //
    // Reuses the EXISTING setAuthProvider/delete server path (now able to target one
    // credential by its user_auth_providers id). Two outcomes mirror createInline:
    //   - already vetted -> the server deletes and returns success -> reload;
    //   - unvetted -> the server returns the IV payload -> hand to the shared IV modal.
    //     The delete is re-run server-side after verification (resume_via = setSecurity)
    //     and id-vetting.js reloads the list.
    // ------------------------------------------------------------------
    var pendingDeleteId = null;

    function deleteConfirmBusy(state) {
        if (!window.jQuery) {
            return;
        }
        var $b = window.jQuery('#delete-passkey-confirm .passkeyDeleteConfirm');
        if (!$b.length) {
            return;
        }

        if (state) {
            // Lock BOTH dimensions BEFORE hiding the label (same technique as the M5 inline
            // create button), so the button keeps its exact size with the spinner centered
            // instead of collapsing to the spinner's width/height.
            $b.css({
                'width': $b.outerWidth() + 'px',
                'height': $b.outerHeight() + 'px',
                'display': 'inline-flex',
                'align-items': 'center',
                'justify-content': 'center'
            });
            $b.addClass('disabled');
            $b.find('.submitText').addClass('hide');
            $b.find('.loading').removeClass('hide').css('display', '');
        } else {
            $b.css({ 'width': '', 'height': '', 'display': '', 'align-items': '', 'justify-content': '' });
            $b.removeClass('disabled');
            $b.find('.submitText').removeClass('hide');
            $b.find('.loading').addClass('hide').css('display', 'none');
        }
    }

    // Deletion must NOT depend on the createPasskey modal (#passkeySecurityUrl /
    // #passkeyRegistrationToken): that modal is only rendered when this device has no
    // passkey - the opposite of when a passkey exists to delete. Resolve both from
    // always-present sources instead: the `account_security` global (show.blade) and the
    // token this modal carries. Falls back to the create-modal fields if ever present.
    function deleteEndpoint() {
        if (typeof account_security !== 'undefined' && account_security) {
            return account_security;
        }
        return securityUrl();
    }

    function deleteCsrfToken() {
        var el = document.getElementById('passkeyDeleteToken');
        if (el && el.value) {
            return el.value;
        }
        var globalToken = window.jQuery ? window.jQuery('[name="_token"]').val() : null;
        return globalToken || csrfToken();
    }

    function deletePasskey(id) {
        return Promise.resolve(window.jQuery.ajax({
            type: 'POST',
            url: deleteEndpoint(),
            data: {
                _token: deleteCsrfToken(),
                action: 'setAuthProvider',
                sub_action: 'delete',
                auth_provider: 'passkey',
                user_auth_provider_id: id
            },
            dataType: 'json'
        })).then(function (data) {
            if (data && data.data && data.data.identity_vetting_required) {
                if (typeof window.checkForIdentityVetting === 'function') {
                    window.checkForIdentityVetting(data);
                }
                var ivError = new Error('identity_vetting_required');
                ivError.identityVetting = true;
                throw ivError;
            }

            if (!data || !data.success) {
                var err = new Error((data && data.msg) || 'passkey delete failed');
                err.responseJSON = data;
                throw err;
            }

            // Deleted. Reload TO the #securityContent hash so the Security tab stays active
            // (a plain reload lands on the default Details tab), exactly as inlineFinish does.
            try { window.location.hash = 'securityContent'; } catch (e) {}
            window.location.reload();
        });
    }

    function confirmDelete() {
        if (busy || pendingDeleteId === null) {
            return;
        }
        busy = true;
        deleteConfirmBusy(true);

        deletePasskey(pendingDeleteId)
            .catch(function (error) {
                busy = false;
                deleteConfirmBusy(false);

                if (window.jQuery) {
                    window.jQuery('#delete-passkey-confirm').modal_close();
                }

                if (error && error.identityVetting) {
                    // IV modal is now showing; the delete resumes after verification.
                    return;
                }

                var message = (error && error.responseJSON && error.responseJSON.msg) || (error && error.message);
                if (window.jQuery && typeof window.jQuery.alertHandler === 'function' && typeof alert_box_failure !== 'undefined') {
                    window.jQuery.alertHandler('', message, alert_box_failure);
                } else {
                    showError(message);
                }
            });
    }

    function initDeleteHandlers() {
        if (!window.jQuery) {
            return;
        }

        // Delegated so it survives regardless of when the list renders. The Remove button
        // only exists on the User's own profile (blade-gated); the server independently
        // enforces ownership + IDOR, so a forged click can never reach another User's row.
        //
        // Namespaced off()+on() so binding is IDEMPOTENT: if this script is ever loaded twice
        // (init would run twice), the confirm handler must still fire ONCE per click - a
        // double bind sent two delete POSTs, and the second hit the already-deleted row
        // (NoAuthProviderAccountLinkedToUserException). One handler, always.
        window.jQuery(document)
            .off('click.passkeyDelete', '.passkeyDeleteBtn')
            .on('click.passkeyDelete', '.passkeyDeleteBtn', function (event) {
                event.preventDefault();
                pendingDeleteId = window.jQuery(this).attr('data-passkey-id');
                window.jQuery('#delete-passkey-confirm').modal_open();
            });

        window.jQuery(document)
            .off('click.passkeyDeleteConfirm', '#delete-passkey-confirm .passkeyDeleteConfirm')
            .on('click.passkeyDeleteConfirm', '#delete-passkey-confirm .passkeyDeleteConfirm', function (event) {
                event.preventDefault();
                confirmDelete();
            });
    }

    function init() {
        // Wired first, and independent of the create modal: deletion must work on the
        // Profile Security page even though the post-login create modal may be absent.
        initDeleteHandlers();

        var modalEl = modal();

        if (!modalEl) {
            return;
        }

        var createBtn = document.getElementById('createPasskeyBtn');
        if (createBtn) {
            createBtn.addEventListener('click', function (event) {
                event.preventDefault();
                create();
            });
        }

        var dismissButtons = modalEl.querySelectorAll('.dismiss_passkey_prompt');
        for (var i = 0; i < dismissButtons.length; i++) {
            dismissButtons[i].addEventListener('click', function (event) {
                event.preventDefault();
                dismiss(this.getAttribute('data-never-ask-again') === '1');
            });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    return {
        init: init,
        create: create,
        createInline: createInline,
        continueWithOptions: continueWithOptions,
        deletePasskey: deletePasskey,
        confirmDelete: confirmDelete,
        dismiss: dismiss,
        slideTo: slideTo
    };
})(window, document);
